import React, { useCallback, useEffect, useRef, useState } from 'react';

import { BrowserProfilePicker } from '../../components/BrowserProfilePicker';
import type { LoopLaunchOptions } from '../../services/landingSkillRouting';
import type { BrowserProfile } from '../../services/loopService';

import { BrowserSelect } from './LoopBrowsers';
import { LoopWatermarkToggle, useLoopWatermarkPermission } from './LoopWatermarkToggle';

import './LoopCreationOptions.css';

// eslint-disable-next-line max-statements
export const useLoopCreationOptions = (profiles: BrowserProfile[], initial?: LoopLaunchOptions) => {
  const [profileId, selectProfile] = useState(initial?.browserProfileId
    || profiles.find((profile) => profile.status === 'online')?.id || '');
  const [recordVideo, setRecordVideo] = useState(initial?.recordVideo ?? true);
  const [watermarkEnabled, setWatermarkEnabled] = useState(initial?.watermarkEnabled ?? true);
  const mayDisableWatermark = useLoopWatermarkPermission();
  const explicitlySelected = useRef(Boolean(initial?.browserProfileId));
  const setProfileId = useCallback((value: string) => {
    explicitlySelected.current = true;
    selectProfile(value);
  }, []);

  useEffect(() => {
    if (explicitlySelected.current || profiles.some((profile) => profile.id === profileId
      && profile.status === 'online')) return;
    const online = profiles.find((profile) => profile.status === 'online');

    selectProfile(online?.id || '');
  }, [profiles, profileId]);

  return { profileId, setProfileId, recordVideo, setRecordVideo,
    watermarkEnabled: mayDisableWatermark ? watermarkEnabled : true, setWatermarkEnabled };
};

export const RecordingToggle: React.FC<{
  value: boolean; onChange: (value: boolean) => void; disabled?: boolean; compact?: boolean;
}> = ({ value, onChange, disabled, compact = false }) => (
  <div className={`loop-recording-option${compact ? ' loop-recording-option--compact' : ''}`}>
    <button type="button" role="switch" aria-checked={value} aria-label="Record browser video"
      className={`loop-recording-switch${value ? ' is-on' : ''}`} disabled={disabled}
      title={compact ? `Record browser video: ${value ? 'on' : 'off'}. Sensitive fields are masked.` : undefined}
      onClick={() => onChange(!value)}>
      <span className="loop-recording-switch__track" aria-hidden="true"><span /></span>
      <span>{compact ? 'Record' : 'Record browser video'}</span>
    </button>
    {!compact && <p className="loop-recording-hint">Sensitive fields are masked in the live view and recording.</p>}
  </div>
);

export const LoopCreationOptions: React.FC<{
  profiles: BrowserProfile[]; profileId: string; onProfileChange: (value: string) => void;
  recordVideo: boolean; onRecordVideoChange: (value: boolean) => void; disabled?: boolean; compact?: boolean;
  watermarkEnabled?: boolean; onWatermarkChange?: (value: boolean) => void;
}> = ({ profiles, profileId, onProfileChange, recordVideo, onRecordVideoChange, disabled, compact = false,
  watermarkEnabled = true, onWatermarkChange }) => (
  <div className={`loop-creation-options${compact ? ' loop-creation-options--compact' : ''}`}>
    {compact ? <BrowserProfilePicker profiles={profiles} value={profileId} onChange={onProfileChange}
      disabled={disabled} compact />
      : <BrowserSelect profiles={profiles} value={profileId} onChange={onProfileChange} disabled={disabled} />}
    <div className="loop-recording-controls">
      <RecordingToggle value={recordVideo} onChange={onRecordVideoChange} disabled={disabled} compact={compact} />
      {onWatermarkChange && <LoopWatermarkToggle value={watermarkEnabled} onChange={onWatermarkChange}
        disabled={disabled} compact={compact} />}
    </div>
  </div>
);
